import AppContext from 'App/AppContext';
import { IPIconButton, IPSeparator, IPSpinner, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import WarningMessage from 'components/Notification/WarningMessage';
import {
  globalStackStylesHeight100PaddingSmall,
  globalStackTokensGapSmall,
  refreshIcon,
  sharepointIcon,
} from 'globalStyles';
import { ColumnDefinition } from 'microsoft-graph';
import ResourceLink from 'models/resourceLink';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GraphScopeId } from 'services/Auth/authConfig';
import { getGraphSharepointErrorMsg, toAppError } from 'services/Graph/graphErrors';
import {
  graphGetFieldsToSelectFromColumn,
  graphGetList,
  graphGetListColumns,
  graphGetListItems,
  graphGetListItemWithFields,
  graphGetListsForSite,
} from 'services/Graph/graphService';
import { IList, IListItem, ISite } from 'services/Graph/SharepointInterfaces';
import { navigateToExternalUrl } from 'utils/url';
import DirectoryParents from './DirectoryParents';
import MyListItems from './MyListItems';
import MyLists from './MyLists';
import MySites from './MySites';
import { IPSpinnerSize } from "baseComponents/Spinner/IPSpinner";

export interface ParentListInformation {
  tenantId?: string;
  siteId: string;
  listId: string;
}

interface ISharepointListItemPickerProps {
  invalidAuthToken: () => void;
  onSelect?: (items: IListItem[]) => void;
  parentList?: ParentListInformation;
  viewMode?: boolean;
  filter?: string;
  filterId?: string;
  existingLinks?: ResourceLink[];
  linkToEdit?: ResourceLink;
  singleSelect?: boolean;
}

const SharepointListItemPicker = (props: ISharepointListItemPickerProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['sharepoint', 'translation']);
  const [selectedSite, setSelectedSite] = useState<ISite | undefined>(undefined);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [lists, setLists] = useState<IList[]>([]);
  const [selectedList, setSelectedList] = useState<IList | undefined>(undefined);
  const [listItems, setListItems] = useState<IListItem[]>([]);
  const [columnDefinitions, setColumnDefinitions] = useState<ColumnDefinition[]>([]);
  const [spError, setSPError] = useState<string | undefined>(undefined);

  //these are only to check whether input is changed to perform a reload
  const [parentInfo, setParentInfo] = useState<ParentListInformation | undefined>(undefined);
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [filterId, setFilterId] = useState<string | undefined>(undefined);

  const checkToken = (token: string): boolean => {
    if (!token) {
      props.invalidAuthToken();
      appContext.showNotification(t('translation:General.Notifications.TryAgainAfterConsent'), true);

      return false;
    }

    return true;
  };

  const loadParentInformation = async () => {
    if (isBusy || !props.parentList) return;
    try {
      setIsBusy(true);
      setSPError(undefined);

      const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, props.parentList.tenantId);
      if (!checkToken(graphInterface.accessToken)) return;
      const _list: IList = await graphGetList(graphInterface.client, props.parentList.siteId, props.parentList.listId);
      _list.tenantId = props.parentList.tenantId;

      const _columnDefinitions = await graphGetListColumns(
        graphInterface.client,
        props.parentList.siteId,
        props.parentList.listId,
      );

      let _listItems: IListItem[] = [];
      if (props.filterId) {
        const li = await graphGetListItemWithFields(
          graphInterface.client,
          props.parentList.siteId,
          props.parentList.listId,
          props.filterId,
          graphGetFieldsToSelectFromColumn(_columnDefinitions),
        );
        if (li) _listItems.push(li);
      } else {
        _listItems = await graphGetListItems(
          graphInterface.client,
          props.parentList.siteId,
          props.parentList.listId,
          graphGetFieldsToSelectFromColumn(_columnDefinitions),
          props.filter,
        );
      }

      _listItems.forEach((l) => (l.tenantId = props.parentList?.tenantId));

      setSelectedList(_list);
      setListItems(_listItems);
      setColumnDefinitions(_columnDefinitions);
    } catch (err) {
      const graphErrorMsg = getGraphSharepointErrorMsg(err, t);
      if (graphErrorMsg) {
        setSPError(graphErrorMsg);
      } else {
        appContext.setError(toAppError(err));
      }
    } finally {
      setIsBusy(false);
    }
  };

  const fetchLists = async () => {
    if (isBusy || selectedSite === undefined || !selectedSite.id) return;
    try {
      setIsBusy(true);
      setSPError(undefined);

      const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, selectedSite.tenantId);
      if (!checkToken(graphInterface.accessToken)) return;
      const _lists: IList[] = await graphGetListsForSite(graphInterface.client, selectedSite.id);
      _lists.forEach((l) => (l.tenantId = selectedSite.tenantId));
      setLists(_lists);
    } catch (err) {
      const graphErrorMsg = getGraphSharepointErrorMsg(err, t);
      if (graphErrorMsg) {
        setSPError(graphErrorMsg);
      } else {
        appContext.setError(toAppError(err));
      }
    } finally {
      setIsBusy(false);
    }
  };

  const fetchItemsForLists = async () => {
    if (
      isBusy ||
      (!props.parentList && selectedSite === undefined) ||
      selectedList === undefined ||
      selectedList.id === undefined ||
      !selectedList.parentReference?.siteId
    )
      return;

    try {
      setIsBusy(true);
      setSPError(undefined);

      const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, selectedList.tenantId);

      if (!checkToken(graphInterface.accessToken)) return;
      const _columnDefinitions = await graphGetListColumns(
        graphInterface.client,
        selectedList.parentReference.siteId,
        selectedList.id,
      );

      let _listItems: IListItem[] = [];
      if (props.filterId) {
        const li = await graphGetListItemWithFields(
          graphInterface.client,
          selectedList.parentReference.siteId,
          selectedList.id,
          props.filterId,
          graphGetFieldsToSelectFromColumn(_columnDefinitions),
        );
        if (li) _listItems.push(li);
      } else {
        _listItems = await graphGetListItems(
          graphInterface.client,
          selectedList.parentReference.siteId,
          selectedList.id,
          graphGetFieldsToSelectFromColumn(_columnDefinitions),
          props.filter,
        );
      }

      _listItems.forEach((l) => (l.tenantId = props.parentList?.tenantId));

      setColumnDefinitions(_columnDefinitions);
      setListItems(_listItems);
    } catch (err) {
      const graphErrorMsg = getGraphSharepointErrorMsg(err, t);
      if (graphErrorMsg) {
        setSPError(graphErrorMsg);
      } else {
        appContext.setError(toAppError(err));
      }
    } finally {
      setIsBusy(false);
    }
  };

  const updateSiteSelection = (site?: ISite) => {
    setSelectedSite(site);
    setSelectedList(undefined);
    setListItems([]);
    setColumnDefinitions([]);
  };

  useEffect(() => {
    fetchItemsForLists();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedList]);

  useEffect(() => {
    if (props.parentList) {
      //changing the parentFolder prop triggers a reload: check whether this is not only a new object but has also different content
      if (
        props.parentList.siteId !== parentInfo?.siteId ||
        props.parentList.listId !== parentInfo?.listId ||
        props.parentList.tenantId !== parentInfo?.tenantId ||
        props.filter !== filter ||
        props.filterId !== filterId
      ) {
        setParentInfo(props.parentList);
        setFilter(props.filter);
        setFilterId(props.filterId);

        loadParentInformation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.parentList, props.filter, props.filterId]);

  useEffect(() => {
    fetchLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite]);

  const onRefresh = () => {
    //Refresh Lists for Group
    if (selectedList) {
      fetchItemsForLists();

      return;
    }
    if (selectedSite) {
      fetchLists();

      return;
    }
  };

  const onOpenSharePoint = () => {
    if (!selectedList?.webUrl) return;
    navigateToExternalUrl(selectedList.webUrl, undefined, '');
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill>
      {/* Body Left and Right*/}
      <IPStack.Item grow>
        <IPStack horizontal styles={globalStackStylesHeight100PaddingSmall} tokens={globalStackTokensGapSmall}>
          {/* Left Groups Part*/}
          {!props.parentList && (
            <IPStack.Item styles={{ root: { width: '30%', padding: 5 } }}>
              <MySites
                onSelect={(item) => updateSiteSelection(item)}
                selectedSite={selectedSite}
                checkToken={checkToken}
                authType={GraphScopeId.Pages}
              />
              <IPSeparator vertical />
            </IPStack.Item>
          )}
          {/* Right Part*/}
          <IPStack.Item grow styles={{ root: { width: !props.parentList ? '70%' : '100%', padding: 5 } }}>
            <IPStack verticalFill>
              {(props.parentList || selectedSite) && (
                <IPStack.Item>
                  <IPStack horizontal wrap horizontalAlign="space-between">
                    <IPStack.Item>
                      <DirectoryParents
                        drive={selectedList}
                        parents={[]}
                        site={selectedSite}
                        onSelectDrive={() => {}}
                        onSelectDriveFolder={() => {}}
                        onBack={() => {}}
                        onSelectSite={updateSiteSelection}
                      />
                    </IPStack.Item>
                    <IPStack.Item>
                      {selectedList && (
                        <IPTooltipHost content={t('sharepoint:CommandBar.OpenSharePoint')}>
                          <IPIconButton iconProps={sharepointIcon} onClick={onOpenSharePoint} />
                        </IPTooltipHost>
                      )}
                      <IPTooltipHost content={t('sharepoint:CommandBar.Refresh')}>
                        <IPIconButton iconProps={refreshIcon} onClick={onRefresh} />
                      </IPTooltipHost>
                    </IPStack.Item>
                  </IPStack>
                </IPStack.Item>
              )}
              {!selectedList && !selectedSite && isBusy && (
                <IPStack verticalFill verticalAlign="center">
                  <IPSpinner size={IPSpinnerSize.large} />
                </IPStack>
              )}
              {spError && !isBusy && (
                <IPStack verticalFill>
                  <WarningMessage message={spError} onDismiss={undefined}></WarningMessage>
                </IPStack>
              )}
              {!spError && selectedSite && selectedList === undefined && (
                <IPStack.Item grow>
                  <MyLists
                    onSelectListItemMode={(item: IList) => setSelectedList(item)}
                    lists={lists}
                    onSelect={(item?: IList) => {
                      setSelectedList(item);
                    }}
                    isListLoading={isBusy}
                  />
                </IPStack.Item>
              )}
              {!spError && selectedList !== undefined && (
                <IPStack.Item grow>
                  <MyListItems
                    items={listItems}
                    columns={columnDefinitions}
                    loading={isBusy}
                    onSelect={(_items: IListItem[]) => {
                      if (props.onSelect) {
                        props.onSelect(_items);
                      }
                    }}
                    viewMode={props.viewMode}
                    existingLinks={props.existingLinks}
                    linkToEdit={props.linkToEdit}
                    singleSelect={props.singleSelect}
                  />
                </IPStack.Item>
              )}
            </IPStack>
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};

export default SharepointListItemPicker;
